import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';

export interface ExcelColumn<T = any> {
  header: string;
  accessor: string | ((row: T) => any);
  formatter?: (value: any) => string;
}

interface ExportToExcelProps<T = any> {
  data: T[];
  filename: string;
  columns: ExcelColumn<T>[];
  disabled?: boolean;
  className?: string;
}

function resolveAccessor<T>(row: T, accessor: string | ((row: T) => any)): any {
  if (typeof accessor === 'function') return accessor(row);
  return (row as Record<string, any>)[accessor];
}

function formatDefaultValue(value: any): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildRowData<T>(row: T, columns: ExcelColumn<T>[]): Record<string, any> {
  const rowData: Record<string, any> = {};
  for (const col of columns) {
    const rawValue = resolveAccessor(row, col.accessor);
    rowData[col.header] = col.formatter ? col.formatter(rawValue) : formatDefaultValue(rawValue);
  }
  return rowData;
}

function buildFilename(filename: string): string {
  if (filename.endsWith('.xlsx')) return filename;
  return `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`;
}

export function ExportToExcel<T = any>({
  data,
  filename,
  columns,
  disabled = false,
  className = '',
}: ExportToExcelProps<T>) {
  const handleExport = () => {
    if (!data || data.length === 0) return;

    const excelData = data.map((row) => buildRowData(row, columns));
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, buildFilename(filename));
  };

  return (
    <button
      onClick={handleExport}
      disabled={disabled || !data || data.length === 0}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
        disabled || !data || data.length === 0
          ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
          : 'bg-green-600 text-white hover:bg-green-700'
      } ${className}`}
      title="Exportar a Excel"
    >
      <Icon icon="mdi:file-excel" className="w-5 h-5" />
      <span>Exportar a Excel</span>
    </button>
  );
}

